import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SearchOffOutlined } from "@mui/icons-material";

import PageTitle from "../../../components/PageTitle";
import { ChoiceChips, EmptyState, SearchField, useGatedNavigate } from "../../../components/ui";
import { SimpleSnackbar } from "../../../components/snackbar";
import { AnnotationDialog } from "./annotation.dialog";
import { ActiveModel } from "./active-model";
import { LIBRARY_FILTER_OPTIONS, isOral, isVehicle } from "./library-filters";
import useActivesLibrary from "./use-actives-library";
import { getProductDetail } from "../../../services/productService";
import { handlePromise } from "../../../utils/handlePromisse";
import { setPreselectProducts } from "../../../services/local-session-storage";
import { ProductModel } from "../../../models/Product.model";
import ActiveCard from "./components/ActiveCard";
import ActiveDrawer from "./components/ActiveDrawer";

// ----------------------------------------------------------------------

type OpenActiveState = { openActive?: { id: number; type?: string; name?: string } };

export const ActivesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateGated, gateDialogs } = useGatedNavigate();
  const {
    items,
    visible,
    filtered,
    total,
    loading,
    error,
    query,
    setQuery,
    filter,
    setFilter,
    showMore,
    hasMore,
    toggleFavorite,
    saveAnnotation,
  } = useActivesLibrary();

  const [drawerItem, setDrawerItem] = useState<ActiveModel | null>(null);
  const [annotationItem, setAnnotationItem] = useState<ActiveModel | null>(null);
  const [snackbar, setSnackbar] = useState("");

  // Deep-link da busca global: abre o drawer do ativo clicado (#busca → ativo).
  // Reage a cada novo openActive e limpa o state em seguida para não reprocessar.
  useEffect(() => {
    const openActive = (location.state as OpenActiveState | null)?.openActive;
    if (!openActive) return;
    const match = items.find((i) => i.id === openActive.id && i.type === openActive.type);
    setDrawerItem(
      match ?? { id: openActive.id, type: openActive.type, name: openActive.name ?? "", favorite: false, mechanismAction: "" }
    );
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, items, navigate]);

  const handleToggleFavorite = async (item: ActiveModel) => {
    const ok = await toggleFavorite(item);
    if (!ok) setSnackbar("Não foi possível atualizar o favorito.");
  };

  const handleSaveAnnotation = async (annotation: string) => {
    if (!annotationItem) return;
    const ok = await saveAnnotation(annotationItem, annotation);
    setSnackbar(ok ? "Anotação salva." : "Erro ao salvar anotação.");
    setAnnotationItem(null);
  };

  // "Usar em nova consulta": para ativo tópico, pré-seleciona o produto na consulta;
  // oral/veículo apenas abrem o fluxo (não são produtos tópicos selecionáveis aqui).
  const handleUseInConsultation = async (active: ActiveModel | null) => {
    if (active && !isOral(active) && !isVehicle(active)) {
      const [result] = await handlePromise(getProductDetail(active.id));
      if (result) {
        const product = result.data as ProductModel;
        setPreselectProducts([{ ...product, idProduct: product.idProduct ?? product.id }]);
      }
    }
    navigateGated("/dashboard/query");
  };

  const drawerCurrent = drawerItem ? (items.find((i) => i.id === drawerItem.id && i.type === drawerItem.type) ?? drawerItem) : null;

  return (
    <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
      <PageTitle title="Ativos" />
      {gateDialogs}
      <SimpleSnackbar show={!!snackbar} message={snackbar} onDismiss={() => setSnackbar("")} />

      <SearchField
        value={query}
        onChange={setQuery}
        placeholder="Buscar por nome, mecanismo, anotação..."
        sx={{ mb: 2, maxWidth: 440 }}
      />

      <ChoiceChips options={LIBRARY_FILTER_OPTIONS} value={filter} onChange={setFilter} sx={{ mb: 1.5 }} />

      <Typography sx={{ mb: "18px", fontSize: "13px", color: "text.secondary" }}>
        <Typography component="span" sx={{ fontWeight: 600, color: "text.primary", fontSize: "inherit" }}>
          {filtered.length} ativos
        </Typography>
        {" "}· de {total} na biblioteca
      </Typography>

      {loading && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.75 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={130} sx={{ borderRadius: "15px" }} />
          ))}
        </Box>
      )}

      {!loading && error && <EmptyState title="Não deu para carregar" description={error} />}

      {!loading && !error && filtered.length === 0 && (
        <EmptyState
          icon={<SearchOffOutlined />}
          title="Nenhum ativo encontrado"
          description="Tente outro termo ou limpe a busca."
          actionLabel="Limpar busca"
          onAction={() => setQuery("")}
        />
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: { xs: 1, md: 1.75 } }}>
            {visible.map((item) => (
              <ActiveCard key={`${item.type}-${item.id}`} item={item} compact={isMobile} onOpen={setDrawerItem} onToggleFavorite={handleToggleFavorite} />
            ))}
          </Box>
          {hasMore && (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Button variant="outlined" onClick={showMore}>
                Carregar mais
              </Button>
            </Box>
          )}
        </>
      )}

      <ActiveDrawer
        item={drawerCurrent}
        open={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        onToggleFavorite={handleToggleFavorite}
        onAnnotate={setAnnotationItem}
        onUse={() => handleUseInConsultation(drawerCurrent)}
      />

      {annotationItem && (
        <AnnotationDialog
          open={!!annotationItem}
          annotation={annotationItem.annotation ?? ""}
          onClose={() => setAnnotationItem(null)}
          onSaveAnnotation={handleSaveAnnotation}
        />
      )}
    </Box>
  );
};
